<template>
    <div class="min-h-screen bg-gray-50 p-4 md:p-8">
        <!-- 页面标题和新建按钮 -->
        <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div class="flex items-center space-x-4">
                <button @click="goBack" class="flex items-center text-reader-text hover:text-primary">
                    <i class="fa fa-arrow-left mr-2"></i>
                    <span class="text-sm">返回</span>
                </button>
            </div>
            <h1 class="text-2xl md:text-3xl font-bold text-gray-800">小说管理</h1>
            <button
                @click="openCreateModal"
                class="mt-4 md:mt-0 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
            >
                <i class="fa fa-plus mr-2"></i> 新建小说
            </button>
        </div>

        <!-- 小说列表 -->
        <div v-if="NovelData" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
                v-for="novel in NovelData"
                :key="novel.id"
                class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
                <!-- 小说封面 -->
                <div class="h-48 overflow-hidden">
                    <img
                        :src="novel.coverImage"
                        alt="小说封面"
                        class="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    >
                </div>

                <!-- 小说信息 -->
                <div class="p-5">
                    <div class="flex items-center justify-between mb-3">
                        <h2 class="text-xl font-semibold text-gray-800 truncate max-w-[200px]">{{ novel.title }}</h2>
                        <div class="flex space-x-2">
                            <button
                                @click="openEditModal(novel)"
                                class="text-blue-500 hover:text-blue-700 p-1.5 rounded-full transition-colors duration-200"
                                title="修改小说"
                            >
                                <i class="fa fa-pencil"></i>
                            </button>
                            <button
                                @click="confirmDelete(novel.id)"
                                class="text-red-500 hover:text-red-700 p-1.5 rounded-full transition-colors duration-200"
                                title="删除小说"
                            >
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </div>

                    <p class="text-gray-600 text-sm mb-4 line-clamp-3">{{ novel.description }}</p>

                    <!-- 操作按钮 -->
                    <div class="flex justify-between">
                        <button
                            @click="viewNovel(novel.id)"
                            class="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                        >
                            查看详情 <i class="fa fa-angle-right ml-1"></i>
                        </button>
                        <span class="text-xs text-gray-500">{{ novel.updatedAt }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 空状态提示 -->
        <div v-else class="text-center py-12">
            <i class="fa fa-book text-5xl text-gray-300 mb-4"></i>
            <h3 class="text-xl font-medium text-gray-700 mb-2">还没有上传小说</h3>
            <p class="text-gray-500 mb-6">点击"新建小说"按钮开始创建你的作品</p>
            <button
                @click="openCreateModal"
                class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
            >
                <i class="fa fa-plus mr-2"></i> 新建小说
            </button>
        </div>

        <!-- 修改小说模态框 -->
        <div v-if="editModalVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div class="p-6 border-b">
                    <div class="flex justify-between items-center">
                        <h3 class="text-xl font-bold text-gray-800">修改小说</h3>
                        <button @click="closeEditModal" class="text-gray-500 hover:text-gray-700">
                            <i class="fa fa-times text-xl"></i>
                        </button>
                    </div>
                </div>

                <div class="p-6">
                    <form @submit.prevent="updateNovel(editedNovel)">
                        <div class="mb-6">
                            <label for="title" class="block text-sm font-medium text-gray-700 mb-1">小说标题</label>
                            <input
                                type="text"
                                id="title"
                                v-model="editedNovel.title"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="输入小说标题"
                                required
                            >
                        </div>
                        <!-- 封面修改区域 -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-1">小说封面</label>

                            <!-- 图片预览 -->
                            <div class="mb-3">
                                <img
                                    v-if="coverPreview || editedNovel.coverImage"
                                    :src="coverPreview || editedNovel.coverImage"
                                    class="h-48 w-32 object-cover rounded-lg border border-gray-300"
                                    alt="封面预览"
                                >
                                <div
                                    v-else
                                    class="h-48 w-32 flex items-center justify-center bg-gray-100 rounded-lg border border-dashed border-gray-300 text-gray-400"
                                >
                                    <i class="fa fa-image text-4xl"></i>
                                </div>
                            </div>

                            <!-- 上传控件 -->
                            <div class="flex items-center space-x-3">
                                <!-- 本地文件上传 -->
                                <label class="flex-1">
                                    <span class="sr-only">选择封面图片</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        @change="handleFileUpload"
                                        class="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
                                    >
                                </label>

                                <span class="text-gray-400">或</span>

                                <!-- URL输入 -->
                                <input
                                    type="text"
                                    v-model="editedNovel.coverImage"
                                    class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="输入封面图片URL"
                                >
                            </div>

                            <div class="mt-2 text-sm text-gray-500">
                                支持JPG/PNG格式，大小不超过2MB
                            </div>
                        </div>

                        <div class="mb-6">
                            <label for="description" class="block text-sm font-medium text-gray-700 mb-1">小说简介</label>
                            <textarea
                                id="description"
                                v-model="editedNovel.description"
                                rows="4"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="输入小说简介"
                                required
                            ></textarea>
                        </div>

                        <!-- 小说类型选择 -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">小说类型</label>
                            <div class="grid grid-cols-3 gap-3">
                                <label
                                    v-for="type in novelTypes"
                                    :key="type.value"
                                    class="flex items-center space-x-2 cursor-pointer p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    <input
                                        type="radio"
                                        v-model="editedNovel.type"
                                        :value="type.value"
                                        class="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                    >
                                    <span class="text-gray-700">{{ type.label }}</span>
                                </label>
                            </div>
                        </div>

                        <div class="flex justify-end space-x-3 pt-4 border-t">
                            <button
                                @click="closeEditModal"
                                class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                取消
                            </button>
                            <button
                                type="submit"
                                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                保存修改
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- 新建小说模态框 (简化版，仅作跳转示例) -->
        <div v-if="createModalVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
                <h3 class="text-xl font-bold text-gray-800 mb-4 text-center">新建小说</h3>
                <p class="text-gray-600 text-center mb-6">点击按钮跳转到新建页面</p>
                <div class="flex justify-center">
                    <button
                        @click="navigateToCreatePage"
                        class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                    >
                        前往新建页面
                    </button>
                </div>
            </div>
        </div>

        <!-- 删除确认模态框 -->
        <div v-if="deleteModalVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
                <div class="text-center mb-6">
                    <i class="fa fa-exclamation-triangle text-4xl text-yellow-500 mb-4"></i>
                    <h3 class="text-xl font-bold text-gray-800 mb-2">确认删除</h3>
                    <p class="text-gray-600">你确定要删除这本小说吗？此操作不可撤销。</p>
                </div>

                <div class="flex justify-center space-x-3">
                    <button
                        @click="cancelDelete"
                        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        取消
                    </button>
                    <button
                        @click="deleteNovel"
                        class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                        确认删除
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue';
import { useRouter } from 'vue-router';
import {novelManageWebSocket} from "@/services/OnlyNovelWebSocket";
import {updateNovel as apiUpdateNovel, deleteNovel as apiDeleteNovel, uploadCover as apiUploadCover} from "@/services/api";
const { connected, novels, loading, error, fetchNovels } = novelManageWebSocket();
const loadData = async () => {
    fetchNovels();
};
const router = useRouter();
// 小说数据 (模拟数据)
const NovelData = computed(() => {
    // 默认项结构（确保包含所有可能字段）
    const defaultItem = {
        id: '0',
        novelId: '0',
        title: '加载中...',
        description: '加载中…',
        coverImage: '/cover_picture/loading.jpg',
        type:'……',
        updatedAt:'2005-01-25 00:00:00'
    };

    // 1. 非数组直接返回默认数组
    if (!Array.isArray(novels.value)) {
        return [defaultItem];
    }

    // 2. 空数组返回默认数组
    if (!novels.value.length) {
        return [defaultItem];
    }

    // 3. 非空数组：用实际数据覆盖默认项的对应字段
    return novels.value.map(item => ({
        ...defaultItem, // 默认值打底
        ...item         // 实际数据覆盖
    }));
});


// 模态框状态
const editModalVisible = ref(false)
const createModalVisible = ref(false)
const deleteModalVisible = ref(false)
const deletingNovelId = ref(null)

const deleteId = ref(null)           // 存储待删除的小说 id
// const baseApiUrl = 'http://localhost:8081';
const baseApiUrl = ''; 
// 小说类型选项
const novelTypes = [
    { value: '玄幻', label: '玄幻' },
    { value: '都市', label: '都市' },
    { value: '历史', label: '历史' }
]

// 编辑中的小说
const editedNovel = ref({
    id: '0',
    novelId: '0',
    title: '加载中...',
    description: '加载中…',
    coverImage: '/cover_picture/loading.jpg',
    type:'玄幻',
    updatedAt:'2005-01-25 00:00:00'
})

// 封面相关状态
const coverPreview = ref('')
const coverFile = ref(null)
const coverChanged = ref(false)
const originalCover = ref('')


// 打开编辑模态框
const openEditModal = (novel) => {
    editedNovel.value = { ...novel }
    originalCover.value = novel.coverImage
    coverPreview.value = ''
    coverFile.value = null
    coverChanged.value = false
    editModalVisible.value = true
}

// 关闭编辑模态框
const closeEditModal = () => {
    editModalVisible.value = false
}

// 处理文件上传
const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (!file) return
    
    
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
    }
    
    // 验证文件大小 (2MB)
    if (file.size > 2 * 1024 * 1024) {
        alert('图片大小不能超过2MB')
        return
    }
    
    coverFile.value = file
    coverChanged.value = true
    
    // 创建预览
    const reader = new FileReader()
    reader.onload = (e) => {
        coverPreview.value = e.target.result
    }
    reader.readAsDataURL(file)
}

// 上传封面图片
const uploadCover = async () => {
    if (!coverFile.value) return null
    
    try {
        const formData = new FormData()
        formData.append('file', coverFile.value)
        console.log(coverFile.value);
        
        const result = await apiUploadCover(formData)
        // Result 结构：{ code: 0, msg: "success", data: ... }
        if (result.code === 0) {
            return result.data // 返回封面路径
        } else {
            alert(result.msg || '封面上传失败')
            return null
        }
    } catch (error) {
        console.error('封面上传失败:', error)
        alert('封面上传失败，请重试')
        return null
    }
}

// 删除旧封面（如果存在且不是默认封面）
const deleteOldCover = async (coverUrl) => {
    try {
        // 提取文件名
        const filename = coverUrl.split('/').pop()
        
        // 跳过默认封面
        if (filename === 'loading.jpg') {
            return
        }
        
        // 这里可以添加删除旧封面的逻辑
        // 由于是静态文件，可能需要后端API或直接文件操作
        console.log('需要删除旧封面:', filename)
        // 在实际项目中，这里可以调用后端API删除旧封面文件
    } catch (error) {
        console.error('删除旧封面失败:', error)
        // 非关键错误，继续执行
    }
}

// 更新小说
const updateNovel = async (thisNovel) => {
    try {
        let finalCoverPath = thisNovel.coverImage
        
        // 如果封面有变化，上传新封面
        if (coverChanged.value && coverFile.value) {
            const newCoverPath = await uploadCover()
            if (newCoverPath) {
                finalCoverPath = newCoverPath
                
                // 删除旧封面（如果存在）
                if (originalCover.value) {
                    await deleteOldCover(originalCover.value)
                }
            } else {
                // 封面上传失败，不继续更新
                return
            }
        }
        
        // 准备更新数据
        const updateData = {
            id: parseInt(thisNovel.id),
            title: thisNovel.title,
            description: thisNovel.description,
            coverImage: finalCoverPath,
            type: thisNovel.type
        }
        
        // 调用后端API更新小说
        const result = await apiUpdateNovel(updateData)
        // Result 结构：{ code: 0, msg: "success", data: ... }
        if (result.code === 0) {
            // 更新成功
            alert('小说更新成功')
            closeEditModal()
            await fetchNovels() // 刷新小说列表
        } else {
            alert(result.msg || '小说更新失败')
        }
    } catch (error) {
        console.error('更新小说失败:', error)
        alert('更新失败，请重试')
    }
}

// 前往新建小说
const openCreateModal = () => {
    router.push({ name: 'NewNovelCreate'});
}

// 跳转到新建页面 (实际项目中可替换为路由跳转)
const navigateToCreatePage = () => {
    createModalVisible.value = false
    console.log("跳转到新建小说页面")
    // 实际项目中使用 router.push('/create-novel')
}

// 确认删除
const confirmDelete = (id) => {
    deletingNovelId.value = id
    deleteModalVisible.value = true
}

// 取消删除
const cancelDelete = () => {
    deletingNovelId.value = null
    deleteModalVisible.value = false
}

// 执行删除
const deleteNovel = async () => {
    console.log(deletingNovelId.value);
    
    if (!deletingNovelId.value) return

    try {
        const result = await apiDeleteNovel(deletingNovelId.value)
        // Result 结构：{ code: 0, msg: "success", data: ... }
        if (result.code === 0) {
            // 删除成功，关闭弹窗，刷新列表
            cancelDelete()
            await fetchNovels() // 刷新小说列表
        } else {
            alert(result.msg || '删除失败')
        }
    } catch (error) {
        console.error('删除失败', error)
        alert('删除失败，请重试')
    }
}

// 查看小说详情 (实际项目中可替换为路由跳转)
const viewNovel = (id) => {
    console.log("查看小说详情，ID:", id)
    // 实际项目中使用 router.push(`/novel/${id}`)
}

// 模拟数据加载 (实际项目中从API获取)
onMounted(() => {
    loadData()
    // 这里可以添加从服务器获取小说数据的逻辑
    console.log("小说数据已加载")
})
// 导航函数
const goBack = () => {
    router.go(-1)
}

</script>

<style>

</style>